import { useRouter } from 'next/router'
import numeral from 'numeral'
import pluralize from 'pluralize'
import { useMemo, useEffect, useState } from 'react'
import { IoInformationCircle } from 'react-icons/io5'

import { useSort } from 'src/providers/SortProvider'
import { useUser } from 'src/providers/UserProvider'

import { Card } from 'src/components/atoms/Card'
import { LoadingSpinner } from 'src/components/atoms/LoadingSpinner'
import { Show } from 'src/components/atoms/Show'
import { Tooltip } from 'src/components/atoms/Tooltip'
import {
  Heading1,
  Heading5,
  Paragraph
} from 'src/components/atoms/Typography/Typography'
import { LabelledCheckbox } from 'src/components/molecules/LabelledCheckbox'
import { NewProjectCard } from 'src/components/molecules/NewProjectCard'
import { PaginationRebrand } from 'src/components/molecules/PaginationRebrand'
import { VStack } from 'src/components/templates/VStack'

import { useListProjectsLazyQuery } from 'src/config/generated/graphql'
import { dashboardPath, listProjectsPath } from 'src/config/paths'
import { useHasPlanterExperience } from 'src/hooks/useHasPlanterExperience'
import { usePagination } from 'src/hooks/usePagination'

import { ProjectsTable } from '../tables/ProjectsTable'
import { buildListProjectsOrderBy } from './helpers'

const LIMIT = 9

export const ListProjects = () => {
  const { sort } = useSort()
  const router = useRouter()
  const { currentPage, limit, offset, onPageClick } = usePagination({
    limit: LIMIT
  })
  const { user } = useUser()
  const [isShowArchived, setIsShowArchived] = useState(false)

  const { hasPlanterExperience, experienceDataCalled, experienceDataLoading } =
    useHasPlanterExperience()

  useEffect(() => {
    if (
      (!hasPlanterExperience &&
        !experienceDataLoading &&
        experienceDataCalled) ||
      !user?.Account?.Planter?.ProjectsCount
    ) {
      router.replace(
        dashboardPath({
          queryParams: {
            selectedTab: 'planter'
          }
        })
      )
    }
  }, [
    experienceDataCalled,
    experienceDataLoading,
    hasPlanterExperience,
    router,
    user
  ])

  const [getProjects, { data: projectsData, loading: projectsLoading }] =
    useListProjectsLazyQuery({
      fetchPolicy: 'cache-and-network'
    })

  useEffect(() => {
    const orderBy = buildListProjectsOrderBy(sort)
    getProjects({
      variables: {
        orderBy,
        limit: LIMIT,
        offset,
        planterId: user?.Account?.PlanterId,
        isShowArchived
      }
    })
  }, [
    getProjects,
    isShowArchived,
    limit,
    offset,
    sort,
    user?.Account?.PlanterId
  ])

  const totalCount = useMemo(() => {
    return projectsData?.ProjectView_aggregate?.aggregate?.count || 0
  }, [projectsData?.ProjectView_aggregate?.aggregate?.count])

  const ProjectZeroState = () => {
    return (
      <Card>
        <Heading5>You don&apos;t have a planting project yet.</Heading5>
        <Paragraph>
          Planting projects are how we show case and tell the stories of how the
          people of Aotearoa are coming together to have a collective impact on
          the affects of climate change.
        </Paragraph>
        <Paragraph className='mb-0!'>
          Planting projects also help us understand your experience as a planter
          You need to create a project to start applying for native trees that
          are funded through Trees That Count.
        </Paragraph>
      </Card>
    )
  }

  return (
    <VStack>
      <LoadingSpinner isLoading={projectsLoading}>
        <div className='flex flex-col items-start'>
          <Heading1 className='mb-[10px]!'>Planting projects</Heading1>
          <div className='mb-5! md:mb-10! flex w-full items-center justify-between'>
            <Paragraph className='mb-0!'>
              {numeral(totalCount).format('0,0')}{' '}
              {pluralize('project', totalCount)} for this account.
            </Paragraph>
            <div className='flex items-center'>
              <LabelledCheckbox
                className='mx-0!'
                id='showArchivedCheckbox'
                checked={isShowArchived}
                onChange={e => setIsShowArchived(e.target.checked)}
                label='Include archived'
              />
              <Tooltip
                placement='bottom'
                overlay={
                  <div className='w-64 px-[5px] text-center'>
                    Projects with no applications from{' '}
                    {new Date().getFullYear() - 2} or earlier will be archived
                    automatically
                  </div>
                }
              >
                <IoInformationCircle
                  onClick={e => e.preventDefault()}
                  className='text-mostlyGreen mb-1 ml-1 inline h-5 w-5'
                />
              </Tooltip>
            </div>
          </div>
          <Show when={totalCount > 0} fallback={<ProjectZeroState />}>
            <VStack className='flex flex-col gap-10'>
              <Card size='large'>
                <ProjectsTable projectsData={projectsData?.ProjectView || []} />
                {totalCount > limit ? (
                  <PaginationRebrand
                    count={totalCount}
                    onChange={(page: number) => onPageClick(page)}
                    page={currentPage}
                    pageSize={limit}
                  />
                ) : null}
              </Card>
              <NewProjectCard returnUrl={listProjectsPath} />
            </VStack>
          </Show>
        </div>
      </LoadingSpinner>
    </VStack>
  )
}
